import { Link } from '@tanstack/react-router'
import { ArrowLeft, Calendar, Clock, MapPin, Ticket } from 'lucide-react'
import type { ReactNode } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useEvent } from '~/entities/event/model/use-events'
import { EventDetailActions } from '~/features/events/components/event-detail-actions'
import { formatEventLongDay, formatEventTimeRange } from '~/features/events/lib/event-date-format'
import { eventPriceLabel } from '~/features/events/lib/event-price-label'
import * as m from '~/paraglide/messages'
import { countryDisplayName } from '~/shared/lib/countries'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'

function MetaRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-center gap-2 text-muted-foreground text-sm sm:text-base">
      <span className="shrink-0 [&_svg]:size-4">{icon}</span>
      <span className="min-w-0 truncate">{children}</span>
    </li>
  )
}

export function EventDetailHeader({ eventId }: { eventId: string }) {
  const { data: business } = useCurrentBusiness()
  const { data: event, isLoading } = useEvent(business?.id ?? '', eventId)

  if (isLoading || !business) {
    return (
      <Card className="flex flex-col gap-6 p-4 sm:p-6 lg:flex-row">
        <Skeleton className="aspect-video w-full rounded-lg lg:w-96 xl:w-[28rem]" />
        <div className="flex flex-1 flex-col gap-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="mt-auto h-9 w-full max-w-lg" />
        </div>
      </Card>
    )
  }

  if (!event) {
    return (
      <Card className="flex flex-col items-start gap-3 p-6">
        <p className="font-medium">{m.eventNotFound()}</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/dashboard/events">
            <ArrowLeft className="h-4 w-4" />
            {m.backToEvents()}
          </Link>
        </Button>
      </Card>
    )
  }

  const isDraft = event.isDraft || event.status === 'Draft'
  const start = new Date(event.startTime)
  const end = new Date(event.endTime)
  const placeName = [event.location.name, event.location.city].filter(Boolean).join(', ')

  return (
    <Card className="flex flex-col gap-6 p-4 sm:p-6 lg:flex-row">
      {event.coverImageUrl ? (
        <img
          src={event.coverImageUrl}
          alt={event.title}
          className="aspect-video w-full shrink-0 rounded-lg bg-muted object-cover lg:w-96 xl:w-[28rem]"
        />
      ) : (
        <div className="flex aspect-video w-full shrink-0 items-center justify-center rounded-lg bg-muted lg:w-96 xl:w-[28rem]">
          <Calendar className="size-12 text-muted-foreground/40" />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="space-y-2">
          {(isDraft || event.status === 'Unlisted' || event.membersOnly || event.isCanceled) && (
            <div className="flex flex-wrap gap-1">
              {isDraft && <Badge variant="secondary">{m.draft()}</Badge>}
              {event.membersOnly ? (
                <Badge variant="outline">{m.eventVisibilityMembers()}</Badge>
              ) : (
                event.status === 'Unlisted' && <Badge variant="outline">{m.unlisted()}</Badge>
              )}
              {event.isCanceled && <Badge variant="destructive">{m.canceled()}</Badge>}
            </div>
          )}
          <h1 className="font-semibold text-2xl text-foreground leading-tight tracking-[-0.025em] sm:text-[30px]">
            {event.title}
          </h1>
        </div>

        <ul className="space-y-1.5">
          <MetaRow icon={<MapPin />}>
            {placeName || `${event.location.city}, ${countryDisplayName(event.location.country)}`}
          </MetaRow>
          <MetaRow icon={<Calendar />}>{formatEventLongDay(start)}</MetaRow>
          <MetaRow icon={<Clock />}>{formatEventTimeRange(start, end)}</MetaRow>
          <MetaRow icon={<Ticket />}>
            <span className="font-medium text-foreground">{eventPriceLabel(event.price)}</span>
          </MetaRow>
        </ul>

        <div className="mt-auto border-t pt-4">
          <EventDetailActions eventId={eventId} />
        </div>
      </div>
    </Card>
  )
}
